/**
 * Permissions Hook.
 *
 * Provides permission checking based on the current user's permissions
 * array (returned by /auth/me). Use this to conditionally show/hide
 * UI elements and nav items based on what the user can do.
 *
 * Usage::
 *
 *   const { hasPermission, hasAnyPermission } = usePermissions();
 *   if (hasPermission("users.manage")) { ... }
 */

import { useAuthStore } from "@/stores/authStore";

export function usePermissions() {
  const user = useAuthStore((s) => s.user);

  // The permissions array from /auth/me response.
  const permissions: string[] =
    (user as { permissions?: string[] } | null)?.permissions || [];

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (...perms: string[]): boolean => {
    return perms.some((p) => permissions.includes(p));
  };

  return { permissions, hasPermission, hasAnyPermission };
}
